import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar/Sidebar';
import Topbar from '../../components/Topbar/Topbar';
import RightSidebar from '../../components/RightSidebar/RightSidebar';
import './Forum.css';

import API_URL from '../../config';


const CATEGORIES = [
  { key: 'all', label: 'Tumu' },
  { key: 'yonetmelik', label: 'Yonetmelik' },
  { key: 'teknik', label: 'Teknik' },
  { key: 'malzeme', label: 'Malzeme' },
  { key: 'kariyer', label: 'Kariyer' },
  { key: 'diger', label: 'Diger' },
];

const Forum = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [theme] = useState(() => localStorage.getItem('projabze_theme') || 'dark');
  const token = localStorage.getItem('projabze_token');

  // Thread list state
  const [threads, setThreads] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [sortBy, setSortBy] = useState('latest');
  const [loadingThreads, setLoadingThreads] = useState(false);

  // New thread form
  const [showNewThread, setShowNewThread] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newBody, setNewBody] = useState('');
  const [newCategory, setNewCategory] = useState('teknik');
  const [newTags, setNewTags] = useState('');
  const [creatingThread, setCreatingThread] = useState(false);

  // Thread detail view
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadDetail, setThreadDetail] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [submittingReply, setSubmittingReply] = useState(false);

  // Right panel
  const [popularThreads, setPopularThreads] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});

  useEffect(() => {
    const stored = localStorage.getItem('projabze_user');
    if (stored) {
      setUser(JSON.parse(stored));
    } else {
      navigate('/');
    }
  }, [navigate]);

  const fetchThreads = useCallback(async () => {
    setLoadingThreads(true);
    try {
      const params = new URLSearchParams({ sort: sortBy });
      if (activeCategory !== 'all') params.set('category', activeCategory);
      const res = await fetch(`${API_URL}/api/forum/threads?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setThreads(data.threads || []);
      if (data.popularThreads) setPopularThreads(data.popularThreads);
      if (data.categoryCounts) setCategoryCounts(data.categoryCounts);
    } catch (err) {
      console.error('Forum fetch error:', err);
    }
    setLoadingThreads(false);
  }, [token, activeCategory, sortBy]);

  useEffect(() => {
    if (token) fetchThreads();
  }, [token, activeCategory, sortBy, fetchThreads]);

  const fetchThreadDetail = async (threadId) => {
    setLoadingDetail(true);
    try {
      const res = await fetch(`${API_URL}/api/forum/threads/${threadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setThreadDetail(data.thread || null);
      setReplies(data.replies || []);
    } catch (err) {
      console.error('Thread detail error:', err);
    }
    setLoadingDetail(false);
  };

  const handleOpenThread = (thread) => {
    setSelectedThread(thread.id);
    fetchThreadDetail(thread.id);
  };

  const handleBackToList = () => {
    setSelectedThread(null);
    setThreadDetail(null);
    setReplies([]);
    setReplyBody('');
    setReplyingTo(null);
  };

  const handleCreateThread = async () => {
    if (!newTitle.trim() || !newBody.trim() || creatingThread) return;
    setCreatingThread(true);
    try {
      const res = await fetch(`${API_URL}/api/forum/threads`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: newTitle,
          body: newBody,
          category: newCategory,
          tags: newTags.split(',').map(t => t.trim()).filter(Boolean)
        })
      });
      const data = await res.json();
      if (res.ok) {
        setThreads(prev => [data.thread, ...prev]);
        setShowNewThread(false);
        setNewTitle('');
        setNewBody('');
        setNewCategory('teknik');
        setNewTags('');
      }
    } catch (err) {
      console.error('Create thread error:', err);
    }
    setCreatingThread(false);
  };

  const handleSubmitReply = async () => {
    if (!replyBody.trim() || submittingReply) return;
    setSubmittingReply(true);
    try {
      const body = { body: replyBody };
      if (replyingTo) body.parentReplyId = replyingTo;
      const res = await fetch(`${API_URL}/api/forum/threads/${selectedThread}/replies`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (res.ok) {
        setReplies(prev => [...prev, data.reply]);
        setReplyBody('');
        setReplyingTo(null);
      }
    } catch (err) {
      console.error('Reply error:', err);
    }
    setSubmittingReply(false);
  };

  const handleVote = async (replyId, direction) => {
    try {
      const res = await fetch(`${API_URL}/api/forum/replies/${replyId}/vote`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ direction })
      });
      const data = await res.json();
      if (res.ok) {
        setReplies(prev => prev.map(r =>
          r.id === replyId ? { ...r, score: data.score, userVote: data.userVote } : r
        ));
      }
    } catch (err) { /* ignore */ }
  };

  const handleMarkSolution = async (replyId) => {
    try {
      const res = await fetch(`${API_URL}/api/forum/threads/${selectedThread}/solve`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ replyId })
      });
      if (res.ok) {
        setReplies(prev => prev.map(r => ({
          ...r,
          isSolution: r.id === replyId
        })));
        setThreadDetail(prev => prev ? { ...prev, isSolved: true, solutionReplyId: replyId } : prev);
      }
    } catch (err) { /* ignore */ }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Az once';
    if (mins < 60) return `${mins}dk`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}sa`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}g`;
    return new Date(dateStr).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
  };

  const getCategoryLabel = (key) => {
    const cat = CATEGORIES.find(c => c.key === key);
    return cat ? cat.label : key;
  };

  if (!user) return null;
  const initials = (user.firstName?.[0] || '') + (user.lastName?.[0] || '');

  // ─── SVG Icon Components ───
  const IconHome = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
  );
  const IconForum = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
  );
  const IconSend = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
  );
  const IconBriefcase = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
  );
  const IconGrid = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
  );
  const IconLayers = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>
  );
  const IconSettings = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
  );
  const IconPlus = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
  );
  const IconArrowLeft = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
  );
  const IconChevronUp = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
  );
  const IconChevronDown = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
  );
  const IconCheck = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
  );
  const IconEye = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
  );
  const IconMessageCircle = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
  );
  const IconCornerDownRight = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 10 20 15 15 20"/><path d="M4 4v7a4 4 0 0 0 4 4h12"/></svg>
  );
  const IconTrendingUp = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
  );
  const IconTag = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
  );

  // ─── Nested reply helper ───
  const getRepliesForParent = (parentId) => {
    return replies.filter(r => r.parentReplyId === parentId);
  };

  const renderReply = (reply, depth = 0) => {
    const replyInitials = (reply.author?.firstName?.[0] || '') + (reply.author?.lastName?.[0] || '');
    const childReplies = getRepliesForParent(reply.id);
    const isAuthor = threadDetail && threadDetail.userId === user?.id;

    return (
      <div key={reply.id} className={`forum-reply ${depth > 0 ? 'forum-reply-nested' : ''}`} style={{ marginLeft: depth > 0 ? `${Math.min(depth, 3) * 24}px` : 0 }}>
        {reply.isSolution && (
          <div className="forum-solution-badge">
            <IconCheck /> Cozum
          </div>
        )}
        <div className="forum-reply-row">
          <div className="forum-reply-votes">
            <button
              className={`forum-vote-btn ${reply.userVote === 'up' ? 'active' : ''}`}
              onClick={() => handleVote(reply.id, 'up')}
            >
              <IconChevronUp />
            </button>
            <span className="forum-vote-score">{reply.score || 0}</span>
            <button
              className={`forum-vote-btn ${reply.userVote === 'down' ? 'active' : ''}`}
              onClick={() => handleVote(reply.id, 'down')}
            >
              <IconChevronDown />
            </button>
          </div>
          <div className="forum-reply-content">
            <div className="forum-reply-header">
              <div className="forum-reply-avatar">
                {reply.author?.avatar ? <img src={API_URL + reply.author.avatar} alt="" /> : replyInitials}
              </div>
              <span className="forum-reply-author">{reply.author?.firstName} {reply.author?.lastName}</span>
              <span className="forum-reply-time">{formatTime(reply.createdAt)}</span>
            </div>
            <div className="forum-reply-body">{reply.body}</div>
            <div className="forum-reply-actions">
              <button className="forum-reply-action" onClick={() => setReplyingTo(replyingTo === reply.id ? null : reply.id)}>
                <IconCornerDownRight /> Yanitla
              </button>
              {isAuthor && !threadDetail?.solutionReplyId && (
                <button className="forum-reply-action solution-action" onClick={() => handleMarkSolution(reply.id)}>
                  <IconCheck /> Cozum Olarak Isaretle
                </button>
              )}
            </div>
            {replyingTo === reply.id && (
              <div className="forum-inline-reply">
                <textarea
                  className="forum-reply-textarea"
                  placeholder="Yanitinizi yazin..."
                  value={replyBody}
                  onChange={e => setReplyBody(e.target.value)}
                  rows={2}
                />
                <div className="forum-inline-reply-actions">
                  <button className="forum-btn-cancel" onClick={() => { setReplyingTo(null); setReplyBody(''); }}>Iptal</button>
                  <button className="forum-btn-submit-sm" onClick={handleSubmitReply} disabled={submittingReply}>
                    {submittingReply ? 'Gonderiliyor...' : 'Gonder'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        {childReplies.map(child => renderReply(child, depth + 1))}
      </div>
    );
  };

  return (
    <div className={`forum-page ${theme}`}>
      <Topbar user={user} onLogout={() => {
        localStorage.removeItem('projabze_token');
        localStorage.removeItem('projabze_user');
        navigate('/');
      }} />
      {/* ─── SIDEBAR ─── */}
      <Sidebar activeId="forum" user={user} onLogout={() => {
        localStorage.removeItem('projabze_token');
        localStorage.removeItem('projabze_user');
        navigate('/');
      }} />

      {/* ─── MAIN CONTENT ─── */}
      <main className="forum-main">
        {!selectedThread ? (
          <>
            {/* Header */}
            <div className="forum-main-header">
              <div>
                <h1>Forum</h1>
                <p>Elektrik muhendisligi tartismalari ve teknik sorular</p>
              </div>
              <button className="forum-new-thread-btn" onClick={() => setShowNewThread(!showNewThread)}>
                <IconPlus /> Yeni Konu
              </button>
            </div>

            {/* New Thread Form */}
            {showNewThread && (
              <div className="forum-new-thread-form">
                <div className="forum-form-group">
                  <label>Baslik</label>
                  <input
                    type="text"
                    placeholder="Konu basligini yazin..."
                    value={newTitle}
                    onChange={e => setNewTitle(e.target.value)}
                  />
                </div>
                <div className="forum-form-group">
                  <label>Icerik</label>
                  <textarea
                    placeholder="Detayli aciklamanizi yazin..."
                    value={newBody}
                    onChange={e => setNewBody(e.target.value)}
                    rows={5}
                  />
                </div>
                <div className="forum-form-row">
                  <div className="forum-form-group">
                    <label>Kategori</label>
                    <select value={newCategory} onChange={e => setNewCategory(e.target.value)}>
                      {CATEGORIES.filter(c => c.key !== 'all').map(c => (
                        <option key={c.key} value={c.key}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="forum-form-group">
                    <label>Etiketler</label>
                    <input
                      type="text"
                      placeholder="etiket1, etiket2, ..."
                      value={newTags}
                      onChange={e => setNewTags(e.target.value)}
                    />
                  </div>
                </div>
                <div className="forum-form-actions">
                  <button className="forum-btn-cancel" onClick={() => setShowNewThread(false)}>Iptal</button>
                  <button className="forum-btn-submit" onClick={handleCreateThread} disabled={creatingThread || !newTitle.trim() || !newBody.trim()}>
                    {creatingThread ? 'Olusturuluyor...' : 'Konuyu Olustur'}
                  </button>
                </div>
              </div>
            )}

            {/* Category Tabs + Sort */}
            <div className="forum-filters">
              <div className="forum-category-tabs">
                {CATEGORIES.map(c => (
                  <button
                    key={c.key}
                    className={`forum-cat-tab ${activeCategory === c.key ? 'active' : ''}`}
                    onClick={() => setActiveCategory(c.key)}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              <select className="forum-sort-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="latest">En Yeni</option>
                <option value="popular">En Populer</option>
                <option value="unanswered">Cevaplanmamis</option>
              </select>
            </div>

            {/* Thread List */}
            <div className="forum-thread-list">
              {loadingThreads && <div className="forum-loading">Yukleniyor...</div>}
              {!loadingThreads && threads.length === 0 && (
                <div className="forum-empty">
                  <div className="forum-empty-icon"><IconForum /></div>
                  <h3>Henuz konu yok</h3>
                  <p>Ilk konuyu siz olusturun ve tartismayi baslatin.</p>
                </div>
              )}
              {threads.map(thread => (
                <div
                  className={`forum-thread-card ${thread.isSolved ? 'solved' : ''}`}
                  key={thread.id}
                  onClick={() => handleOpenThread(thread)}
                >
                  <div className="forum-thread-left">
                    <div className="forum-thread-avatar">
                      {thread.author?.avatar ? <img src={API_URL + thread.author.avatar} alt="" /> : (thread.author?.firstName?.[0] || '') + (thread.author?.lastName?.[0] || '')}
                    </div>
                  </div>
                  <div className="forum-thread-body">
                    <div className="forum-thread-title-row">
                      <h3 className="forum-thread-title">{thread.title}</h3>
                      {thread.isSolved && (
                        <span className="forum-solved-tag"><IconCheck /> Cozuldu</span>
                      )}
                    </div>
                    <div className="forum-thread-meta">
                      <span className="forum-thread-author">{thread.author?.firstName} {thread.author?.lastName}</span>
                      <span className="forum-thread-dot">&middot;</span>
                      <span className="forum-thread-time">{formatTime(thread.createdAt)}</span>
                      <span className="forum-thread-dot">&middot;</span>
                      <span className={`forum-category-badge cat-${thread.category}`}>
                        {getCategoryLabel(thread.category)}
                      </span>
                    </div>
                    {thread.tags?.length > 0 && (
                      <div className="forum-thread-tags">
                        {thread.tags.map((tag, i) => (
                          <span key={i} className="forum-tag"><IconTag /> {tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="forum-thread-stats">
                    <div className="forum-thread-stat">
                      <IconMessageCircle />
                      <span>{thread.replyCount || 0}</span>
                    </div>
                    <div className="forum-thread-stat">
                      <IconEye />
                      <span>{thread.viewCount || 0}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          /* ─── Thread Detail View ─── */
          <div className="forum-detail">
            <button className="forum-back-btn" onClick={handleBackToList}>
              <IconArrowLeft /> Konulara Don
            </button>

            {loadingDetail ? (
              <div className="forum-loading">Yukleniyor...</div>
            ) : threadDetail ? (
              <>
                <div className="forum-detail-header">
                  <div className="forum-detail-title-row">
                    <h1 className="forum-detail-title">{threadDetail.title}</h1>
                    {threadDetail.isSolved && (
                      <span className="forum-solved-tag lg"><IconCheck /> Cozuldu</span>
                    )}
                  </div>
                  <div className="forum-detail-meta">
                    <div className="forum-detail-avatar">
                      {threadDetail.author?.avatar ? <img src={API_URL + threadDetail.author.avatar} alt="" /> : (threadDetail.author?.firstName?.[0] || '') + (threadDetail.author?.lastName?.[0] || '')}
                    </div>
                    <span className="forum-detail-author">{threadDetail.author?.firstName} {threadDetail.author?.lastName}</span>
                    <span className="forum-detail-dot">&middot;</span>
                    <span className="forum-detail-time">{formatTime(threadDetail.createdAt)}</span>
                    <span className="forum-detail-dot">&middot;</span>
                    <span className={`forum-category-badge cat-${threadDetail.category}`}>
                      {getCategoryLabel(threadDetail.category)}
                    </span>
                  </div>
                  {threadDetail.tags?.length > 0 && (
                    <div className="forum-thread-tags" style={{ marginTop: '.5rem' }}>
                      {threadDetail.tags.map((tag, i) => (
                        <span key={i} className="forum-tag"><IconTag /> {tag}</span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="forum-detail-body">
                  {threadDetail.body}
                </div>

                {/* Replies */}
                <div className="forum-replies-section">
                  <h2 className="forum-replies-title">
                    <IconMessageCircle /> {replies.length} Yanit
                  </h2>

                  <div className="forum-replies-list">
                    {replies.filter(r => !r.parentReplyId).map(reply => renderReply(reply))}
                  </div>

                  {/* Reply Input */}
                  {!replyingTo && (
                    <div className="forum-reply-box">
                      <div className="forum-reply-box-avatar">
                        {user.avatar ? <img src={API_URL + user.avatar} alt="" /> : initials}
                      </div>
                      <div className="forum-reply-box-body">
                        <textarea
                          className="forum-reply-textarea"
                          placeholder="Yanitinizi yazin..."
                          value={replyBody}
                          onChange={e => setReplyBody(e.target.value)}
                          rows={3}
                        />
                        <div className="forum-reply-box-actions">
                          <button className="forum-btn-submit" onClick={handleSubmitReply} disabled={submittingReply || !replyBody.trim()}>
                            {submittingReply ? 'Gonderiliyor...' : 'Yanit Gonder'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="forum-empty">
                <h3>Konu bulunamadi</h3>
              </div>
            )}
          </div>
        )}
      </main>

      {/* ─── RIGHT PANEL ─── */}
      <RightSidebar />
    </div>
  );
};

export default Forum;
